import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, FormControlLabel, Checkbox, Stack,
  FormControl, FormLabel, RadioGroup, Radio, Select,
  MenuItem, FormGroup, Chip, Box,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

const SCOPE_ALL      = 'all';
const SCOPE_ACTIVE   = 'active';
const SCOPE_ARCHIVED = 'archived';

/**
 * HistoryReportDialog lets the user configure and export a maintenance history report.
 *
 * It supports date range selection, vehicle scoping (all/active/archived), multi-vehicle
 * selection, multi-provider filtering, and a notes toggle, then calls "onExport" with the
 * resulting parameters. When specific vehicles are selected they override the scope filter
 * on the backend.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is visible.
 * @param {() => void} props.onClose - Called when the dialog is closed.
 * @param {(params: Object)} props.onExport - Called with export query params when the user clicks "Export".
 * @param {Array<Object>} [props.vehicles] - List of vehicles used for search and scoping.
 * @param {Array<Object>} [props.providers] - List of providers to filter history by.
 * @returns {JSX.Element}
 */
export default function HistoryReportDialog({
  open, onClose, onExport, vehicles = [], providers = []
}) {
  const [mode, setMode] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [selectedDescriptions, setSelectedDescriptions] = useState([]);
  const [includeNotes, setIncludeNotes] = useState(true);

  const [scope, setScope] = useState(SCOPE_ALL);
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [selectedProviderIds, setSelectedProviderIds] = useState([]);

  const THIS_YEAR = new Date().getFullYear();

  /**
   * Returns true if the string is a four-digit year between 1980 and next year.
   * Used to treat year tokens in the vehicle search input.
   * @param {string} s
   * @returns {boolean}
   */
  const isYear = (s) => /^\d{4}$/.test(s) && +s >= 1980 && +s <= THIS_YEAR + 1;

  /**
   * Parses a vehicle search query into a dedicated year and remaining word tokens.
   * Supports inputs like "2019 toyota camry", where 2019 becomes "year" and rest becomes "words".
   *
   * @param {string} q
   * @returns {{ year: number | null, words: string[] }}
   */
  const parseVehicleQuery = (q) => {
    const tokens = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    let year = null;
    const words = [];
    for (const t of tokens) {
      if (year === null && isYear(t)) {
        year = +t;
      } else {
        words.push(t);
      }
    }
    return { year, words };
  };

  /**
   * Returns active or archived vehicles depending on the scope.
   *
   * @returns {Array<Object>}
   */
  const visibleVehicles = useMemo(() => {
    if (scope === SCOPE_ACTIVE)   return vehicles.filter(v => !v.IsArchived);
    if (scope === SCOPE_ARCHIVED) return vehicles.filter(v =>  v.IsArchived);
    return vehicles;
  }, [vehicles, scope]);

  /**
   * Allows the user to filter vehicles by Make, Model, VIN, License Plate,
   * and Comments field.
   */
  const filteredVehicles = useMemo(() => {
    const q = vehicleSearch.trim().toLowerCase();
    if (!q) return visibleVehicles;

    const { year, words } = parseVehicleQuery(q);

    return visibleVehicles.filter((v) => {
      const make = (v.Make || '').toLowerCase();
      const model = (v.Model || '').toLowerCase();
      const makeModel = `${make} ${model}`.trim();

      if ((v.VIN || '').toLowerCase().includes(q)) {
        return true;
      }
      if ((v.License || '').toLowerCase().includes(q)) {
        return true;
      }

      if (year !== null && Number(v.Year) !== year) {
        return false;
      }

      const allWordsMatch = words.every(w => makeModel.includes(w));

      return words.length ? allWordsMatch : (year !== null);
    });
  }, [visibleVehicles, vehicleSearch]);

  const vehicleOptions = useMemo(
    () => filteredVehicles.map(v => ({
      label: `${v.Make} ${v.Model} ${v.Year} - ${v.License || v.VIN || v.VehicleId}`,
      value: v.VehicleId,
      isArchived: v.IsArchived,
    })),
    [filteredVehicles]
  );

  /**
   * Builds export query parameters from the current dialog state
   * and calls "onExport".
   *
   * Encodes date range, vehicle scope, specific vehicle selections, provider filter,
   * and whether notes should be included. Specific vehicle selections override the scope
   * filter; if none are selected, the scope maps to an `archived` flag.
   */
  const handleExport = () => {
    const params = {
      ...(mode === 'custom' && from ? { from } : {}),
      ...(mode === 'custom' && to   ? { to }   : {}),
      includeNotes,
    };

    if (selectedVehicles.length > 0) {
      params.vehicleIds = selectedVehicles.map(v => v.value).join(',');
    } else if (scope === SCOPE_ACTIVE) {
      params.archived = '0';
    } else if (scope === SCOPE_ARCHIVED) {
      params.archived = '1';
    }

    if (selectedProviderIds.length > 0) {
      params.providerIds = selectedProviderIds.join(',');
    }

    const cleanedDescriptions = selectedDescriptions
      .map(s => (typeof s === 'string' ? s.trim() : ''))
      .filter(Boolean);
    if (cleanedDescriptions.length > 0) {
      params.descriptions = cleanedDescriptions;
    }

    onExport(params);
  };

  const disableDates = mode !== 'custom';

  const providerNameById = useMemo(() => {
    const map = new Map();
    for (const p of providers) map.set(p.ProviderId, p.ProviderName);
    return map;
  }, [providers]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Maintenance History</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <FormControl sx={{ mb: 2 }}>
          <FormLabel>Date Range</FormLabel>
          <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
            <FormControlLabel value="all" control={<Radio />} label="All dates" />
            <FormControlLabel value="custom" control={<Radio />} label="Custom" />
          </RadioGroup>
        </FormControl>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField label="From (YYYY-MM-DD)" type="date" value={from}
            onChange={(e) => setFrom(e.target.value)} disabled={disableDates}
            sx={{ mr: 2, mb: 2 }} InputLabelProps={{ shrink: true }} />
          <TextField label="To (YYYY-MM-DD)" type="date" value={to}
            onChange={(e) => setTo(e.target.value)} disabled={disableDates}
            sx={{ mb: 2 }} InputLabelProps={{ shrink: true }} />
        </Stack>

        <FormControl sx={{ mb: 1 }}>
          <FormLabel>Vehicle Scope</FormLabel>
          <RadioGroup row value={scope} onChange={(e) => setScope(e.target.value)}>
            <FormControlLabel value={SCOPE_ALL}      control={<Radio />} label="All" />
            <FormControlLabel value={SCOPE_ACTIVE}   control={<Radio />} label="Active only" />
            <FormControlLabel value={SCOPE_ARCHIVED} control={<Radio />} label="Archived only" />
          </RadioGroup>
        </FormControl>

        <Autocomplete
          multiple
          sx={{ mb: 2 }}
          options={vehicleOptions}
          value={selectedVehicles}
          getOptionLabel={(opt) => opt?.label ?? ''}
          isOptionEqualToValue={(a, b) => a?.value === b?.value}
          onInputChange={(_, val) => setVehicleSearch(val)}
          inputValue={vehicleSearch}
          onChange={(_, val) => setSelectedVehicles(val)}
          filterOptions={(options) => options}
          renderOption={(props, opt) => (
            <li {...props} key={opt.value}>
              {opt.label}
              {opt.isArchived ? <Chip size="small" sx={{ ml: 1 }} label="Archived" /> : null}
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((opt, index) => (
              <Chip
                {...getTagProps({ index })}
                key={opt.value}
                size="small"
                label={opt.label}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={selectedVehicles.length > 0
                ? `Specific vehicles (${selectedVehicles.length} selected)`
                : 'Specific vehicles (optional)'}
              placeholder="Type make, model, plate, VIN, or year..."
            />
          )}
        />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <Select
            multiple
            displayEmpty
            value={selectedProviderIds}
            onChange={(e) => {
              const val = e.target.value;
              setSelectedProviderIds(typeof val === 'string' ? val.split(',').map(Number) : val);
            }}
            renderValue={(selected) => {
              if (!selected || selected.length === 0) {
                return <em>All providers</em>;
              }
              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((id) => (
                    <Chip key={id} size="small" label={providerNameById.get(id) ?? id} />
                  ))}
                </Box>
              );
            }}
          >
            {providers?.map(p => (
              <MenuItem key={p.ProviderId} value={p.ProviderId}>
                <Checkbox checked={selectedProviderIds.indexOf(p.ProviderId) > -1} />
                {p.ProviderName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={selectedDescriptions}
          onChange={(_, val) => setSelectedDescriptions(val)}
          renderTags={(value, getTagProps) =>
            value.map((opt, index) => (
              <Chip
                {...getTagProps({ index })}
                key={`${opt}-${index}`}
                size="small"
                label={opt}
              />
            ))
          }
          sx={{ mb: 2 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={selectedDescriptions.length > 0
                ? `Service descriptions (${selectedDescriptions.length} selected)`
                : 'Service descriptions (optional)'}
              placeholder="Type keywords or phrases (press Enter to add)"
            />
          )}
        />

        <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={includeNotes} onChange={(e) => setIncludeNotes(e.target.checked)} />}
            label="Include Notes column"
          />
        </FormGroup>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleExport}>Export</Button>
      </DialogActions>
    </Dialog>
  );
}
